'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useContext } from 'react';
import { useWatch } from 'react-hook-form';
import { CartContext } from '@/entities/cart';
import { VenueCardInfo } from '@/entities/venue/ui/venue-card/venue-card-info';
import { VenueStatus } from '@/entities/venue/ui/venue-status';
import { cn } from '@/shared/lib/cn';
import { useAddressFormatter } from '@/shared/lib/hooks/useAddressFormater';
import { Address } from '@/shared/model/Address';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Icon } from '@/shared/ui/icon';
import { Media } from '@/shared/ui/media';
import { Tags } from '@/shared/ui/tags';
import './styles.scss';

import { DeliveryType } from '@/features/enums/DeliveryType';
import { useGetVenueHrefAttributes } from '../../hooks/useGetVenueHrefAttributes';

type Props = {
    id?: string;
    title?: string;
    url: string;
    apiPartnerSettingsId: string;
    imageUrl?: string;
    isImageTagsVisible?: boolean;
    tags?: Parameters<typeof Tags>[0]['tags'];
    address?: Address;
    status?: Omit<Parameters<typeof VenueStatus>[0], 'locale' | 't'>;
    isSelected: boolean;
    deliveryType: DeliveryType;
};
export const SelectVenueModalVenue = ({
    title,
    address,
    status,
    imageUrl,
    isImageTagsVisible,
    tags,
    isSelected,
    apiPartnerSettingsId,
    deliveryType,
}: Props) => {
    const t = useTranslations();
    const locale = useLocale();
    const formattedAddress = useAddressFormatter(address);
    const cart = useContext(CartContext);
    const deliveryAddress = useWatch({
        control: cart?.formMethods?.control,
        name: 'deliveryMethod.address',
    });

    const getVenueHrefAttributes = useGetVenueHrefAttributes();
    const { href, onClick, onKeyDown } = getVenueHrefAttributes(apiPartnerSettingsId, {
        address: deliveryAddress,
        deliveryType,
    });

    return (
        <a href={href} onClick={onClick} onKeyDown={onKeyDown} className="select-venue-modal__venue-link">
            <Card
                className={cn(
                    'select-venue-modal__venue',
                    isSelected && 'select-venue-modal__venue--selected',
                )}
                data-testid="select-venue-modal-venue"
                shadow
            >
                {imageUrl && (
                    <div className="select-venue-modal__venue-image">
                        {isImageTagsVisible && <Tags tags={tags} />}
                        <Media
                            data={{
                                url: imageUrl,
                                alt: `venue-${title}-image`,
                            }}
                            sizes="19.5rem"
                            data-testid="select-venue-modal-venue-image"
                        />
                    </div>
                )}
                <div className="select-venue-modal__venue-wrapper">
                    <div className="select-venue-modal__venue-content">
                        <span
                            className="select-venue-modal__venue-content-title"
                            data-testid="select-venue-modal-venue-title"
                        >
                            {title}
                        </span>
                        {formattedAddress && (
                            <VenueCardInfo
                                icon={(svgProps) => <Icon name="scan" {...svgProps} />}
                                data-testid="select-venue-modal-venue-address"
                            >
                                {formattedAddress}
                            </VenueCardInfo>
                        )}
                        {status && (
                            <VenueStatus
                                {...status}
                                t={t}
                                locale={locale}
                                data-testid="select-venue-modal-venue-status"
                            />
                        )}
                    </div>
                    <Button size="default" data-testid="select-venue-modal-venue-select-button">
                        {isSelected && <Icon name="check" />}
                        {t(`WORDS.SELECT${isSelected ? 'ED' : ''}`)}
                    </Button>
                </div>
            </Card>
        </a>
    );
};
